import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  People,
  Storage,
  Api,
  CheckCircle,
  Error as ErrorIcon,
  TrendingUp,
  CloudQueue,
  Memory,
  Speed,
  FolderOpen,
  Login,
  Warning,
  Schedule,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { metricsAPI, LoginActivity, ErrorActivity } from '../services/api';

interface MetricsOverview {
  tenants: {
    total_tenants: number;
    active_tenants: number;
    suspended_tenants: number;
  };
  usage: {
    total_api_calls: number;
    total_users: number;
    total_storage_gb: number;
  };
  health: {
    cpu_usage_percent: number;
    memory_usage_percent: number;
    disk_usage_percent: number;
    postgis_healthy: boolean;
    redis_healthy: boolean;
    nginx_healthy: boolean;
  };
}

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: { value: string; positive: boolean };
  chips?: { label: string; color: 'success' | 'warning' | 'error' }[];
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, trend, chips }) => {
  const { isDark } = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: '16px',
        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa',
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: isDark ? 'rgba(50, 130, 139, 0.15)' : 'rgba(50, 130, 139, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#32828B',
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: '0.85rem',
            fontWeight: 500,
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
          }}
        >
          {title}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: '2.25rem',
          fontWeight: 600,
          color: isDark ? '#ffffff' : '#0f172a',
          letterSpacing: '-1px',
          mb: 1,
        }}
      >
        {value}
      </Typography>

      {subtitle && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <TrendingUp
            sx={{
              fontSize: '1rem',
              color: trend.positive ? '#41A35F' : '#ef4444',
            }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: trend.positive ? '#41A35F' : '#ef4444',
            }}
          >
            {trend.value}
          </Typography>
        </Box>
      )}

      {chips && chips.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {chips.map((chip, i) => (
            <Chip
              key={i}
              label={chip.label}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 500,
                bgcolor: chip.color === 'success'
                  ? 'rgba(65, 163, 95, 0.15)'
                  : chip.color === 'warning'
                  ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(239, 68, 68, 0.15)',
                color: chip.color === 'success'
                  ? '#41A35F'
                  : chip.color === 'warning'
                  ? '#f59e0b'
                  : '#ef4444',
                border: 'none',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Health Metric Component
interface HealthMetricProps {
  label: string;
  value: number;
  icon: React.ReactNode;
}

const HealthMetric: React.FC<HealthMetricProps> = ({ label, value, icon }) => {
  const { isDark } = useTheme();

  const getColor = (v: number) => {
    if (v < 70) return '#41A35F';
    if (v < 85) return '#f59e0b';
    return '#ef4444';
  };

  const color = getColor(value);

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: '12px',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
            {icon}
          </Box>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '1.1rem',
            fontWeight: 600,
            color,
          }}
        >
          {value.toFixed(1)}%
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Box
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(value, 100)}%`,
            borderRadius: 3,
            bgcolor: color,
            transition: 'width 0.5s ease',
          }}
        />
      </Box>
    </Box>
  );
};

// Service Status Component
interface ServiceStatusProps {
  name: string;
  healthy: boolean;
}

const ServiceStatus: React.FC<ServiceStatusProps> = ({ name, healthy }) => {
  const { isDark } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: '10px',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 500,
          color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
        }}
      >
        {name}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: '6px',
          bgcolor: healthy
            ? 'rgba(65, 163, 95, 0.12)'
            : 'rgba(239, 68, 68, 0.12)',
        }}
      >
        {healthy ? (
          <CheckCircle sx={{ fontSize: '0.9rem', color: '#41A35F' }} />
        ) : (
          <ErrorIcon sx={{ fontSize: '0.9rem', color: '#ef4444' }} />
        )}
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: healthy ? '#41A35F' : '#ef4444',
          }}
        >
          {healthy ? 'Healthy' : 'Unhealthy'}
        </Typography>
      </Box>
    </Box>
  );
};

// Activity Feed Item Component
interface ActivityItemProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
  time: string;
  status?: 'success' | 'failed' | 'warning';
}

const ActivityItem: React.FC<ActivityItemProps> = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  time,
  status,
}) => {
  const { isDark } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1.5,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {status && (
            <Chip
              label={status}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor:
                  status === 'success'
                    ? 'rgba(65, 163, 95, 0.15)'
                    : status === 'warning'
                    ? 'rgba(245, 158, 11, 0.15)'
                    : 'rgba(239, 68, 68, 0.15)',
                color:
                  status === 'success'
                    ? '#41A35F'
                    : status === 'warning'
                    ? '#f59e0b'
                    : '#ef4444',
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '0.7rem',
          color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
          flexShrink: 0,
        }}
      >
        {time}
      </Typography>
    </Box>
  );
};

// Format relative time
const formatRelativeTime = (timestamp: string): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const Dashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [metrics, setMetrics] = useState<MetricsOverview | null>(null);
  const [loginActivity, setLoginActivity] = useState<LoginActivity[]>([]);
  const [errorActivity, setErrorActivity] = useState<ErrorActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
    loadActivityFeeds();
    const metricsInterval = setInterval(loadMetrics, 30000);
    const activityInterval = setInterval(loadActivityFeeds, 60000);
    return () => {
      clearInterval(metricsInterval);
      clearInterval(activityInterval);
    };
  }, []);

  const loadMetrics = async () => {
    try {
      const data = await metricsAPI.getOverview();
      setMetrics(data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load metrics');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityFeeds = async () => {
    try {
      const [loginData, errorData] = await Promise.all([
        metricsAPI.getLoginActivity(10),
        metricsAPI.getErrorActivity(10),
      ]);
      setLoginActivity(loginData.logins || []);
      setErrorActivity(errorData.errors || []);
    } catch (err) {
      console.error('Error loading activity feeds:', err);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: '#32828B' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          borderRadius: '12px',
          bgcolor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}
      >
        {error}
      </Alert>
    );
  }

  if (!metrics) {
    return (
      <Alert
        severity="info"
        sx={{
          borderRadius: '12px',
          bgcolor: 'rgba(50, 130, 139, 0.1)',
          border: '1px solid rgba(50, 130, 139, 0.2)',
        }}
      >
        No metrics available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<CloudQueue sx={{ fontSize: '1.3rem' }} />}
            title="Organizations"
            value={metrics.tenants.total_tenants}
            chips={[
              { label: `${metrics.tenants.active_tenants} Active`, color: 'success' },
              { label: `${metrics.tenants.suspended_tenants} Suspended`, color: 'warning' },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<Api sx={{ fontSize: '1.3rem' }} />}
            title="API Calls Today"
            value={(metrics.usage.total_api_calls || 0).toLocaleString()}
            subtitle="Across all tenants"
            trend={{ value: '+12%', positive: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<People sx={{ fontSize: '1.3rem' }} />}
            title="Active Users"
            value={metrics.usage.total_users || 0}
            subtitle="Unique users today"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<Storage sx={{ fontSize: '1.3rem' }} />}
            title="Total Storage"
            value={`${(metrics.usage.total_storage_gb || 0).toFixed(1)} GB`}
            subtitle="Platform storage"
          />
        </Grid>
      </Grid>

      {/* System Health Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            mb: 2,
          }}
        >
          System Resources
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <HealthMetric
              label="CPU Usage"
              value={metrics.health.cpu_usage_percent || 0}
              icon={<Speed sx={{ fontSize: '1.1rem' }} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <HealthMetric
              label="Memory Usage"
              value={metrics.health.memory_usage_percent || 0}
              icon={<Memory sx={{ fontSize: '1.1rem' }} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <HealthMetric
              label="Disk Usage"
              value={metrics.health.disk_usage_percent || 0}
              icon={<FolderOpen sx={{ fontSize: '1.1rem' }} />}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Service Status Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            mb: 2,
          }}
        >
          Service Status
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ServiceStatus name="PostgreSQL" healthy={metrics.health.postgis_healthy} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ServiceStatus name="Redis" healthy={metrics.health.redis_healthy} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ServiceStatus name="Nginx" healthy={metrics.health.nginx_healthy} />
          </Grid>
        </Grid>
      </Box>

      {/* Activity Feeds Section */}
      <Grid container spacing={3}>
        {/* Login Activity Feed */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '16px',
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(50, 130, 139, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#32828B',
                }}
              >
                <Login sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                }}
              >
                Recent Login Activity
              </Typography>
            </Box>

            <Divider sx={{ mb: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

            {loginActivity.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Schedule sx={{ fontSize: '2rem', color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  No recent login activity
                </Typography>
              </Box>
            ) : (
              loginActivity.map((login, index) => (
                <ActivityItem
                  key={index}
                  icon={<Login sx={{ fontSize: '1rem' }} />}
                  iconBg={login.status === 'success' ? 'rgba(65, 163, 95, 0.15)' : 'rgba(239, 68, 68, 0.15)'}
                  iconColor={login.status === 'success' ? '#41A35F' : '#ef4444'}
                  title={login.username || 'Unknown User'}
                  subtitle={`${login.ip_address} - ${login.path.split('/').pop() || 'auth'}`}
                  time={formatRelativeTime(login.timestamp)}
                  status={login.status}
                />
              ))
            )}
          </Box>
        </Grid>

        {/* Error Activity Feed */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '16px',
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ef4444',
                }}
              >
                <Warning sx={{ fontSize: '1.2rem' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                }}
              >
                Recent Errors
              </Typography>
              {errorActivity.length > 0 && (
                <Chip
                  label={errorActivity.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(239, 68, 68, 0.15)',
                    color: '#ef4444',
                    ml: 'auto',
                  }}
                />
              )}
            </Box>

            <Divider sx={{ mb: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

            {errorActivity.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: '2rem', color: '#41A35F', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  No recent errors - all systems running smoothly
                </Typography>
              </Box>
            ) : (
              errorActivity.map((err, index) => (
                <ActivityItem
                  key={index}
                  icon={<ErrorIcon sx={{ fontSize: '1rem' }} />}
                  iconBg={err.error_type === 'server_error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)'}
                  iconColor={err.error_type === 'server_error' ? '#ef4444' : '#f59e0b'}
                  title={`${err.status_code} - ${err.action}`}
                  subtitle={`${err.method} ${err.path.split('?')[0]}`}
                  time={formatRelativeTime(err.timestamp)}
                  status={err.error_type === 'server_error' ? 'failed' : 'warning'}
                />
              ))
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
